import { FullPage } from 'components/Layout/FullPageLayout';
import React, { useCallback } from 'react';
import { FragmentEnv } from 'utils/env';
import { useAuth0 } from '@auth0/auth0-react';

import { Button } from '@fragment/ui/src/components/Button/BaseButton/Button';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { List, ListItem, Marker } from '@fragment/ui/src/components/List';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { Logo } from '@fragment/ui/src/components/Logo/Logo';
import { Text } from '@fragment/ui/src/components/Text';

type LoadingProps = {
  loading?: boolean;
};

type Props =
  | (LoadingProps & {
      message?: string;
      errorMessage?: never;
    })
  | (LoadingProps & {
      message?: never;
      errorMessage?: string;
    });

export const LoginView = ({
  loading = false,
  errorMessage,
  message,
}: Props) => {
  const { loginWithRedirect } = useAuth0();
  const handler = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: {
          audience: FragmentEnv.auth0.globalApiAudience,
          connection: 'google-oauth2',
          scope: 'openid email profile offline_access',
        },
      }),
    [loginWithRedirect]
  );

  const markerClass = `text-main-600 ${
    loading ? 'group-hover:text-main-600' : 'group-hover:text-main'
  }` as const;
  const textColorClass = loading ? 'text-main-600' : 'text-main';
  const shouldRenderMessageContainer = !!message || !!errorMessage;

  return (
    <FullPage.Layout>
      <Logo />
      <div className="mt-[4em]">
        {loading && (
          <Text as="span" fontSize="text-fxl" color={textColorClass}>
            <LoadingText text="Finding Account" />
          </Text>
        )}
        {!loading && (
          <List
            type="ordered"
            fontFamily="font-FragmentMono"
            fontSize="text-flg tablet:text-fxl"
          >
            <div className="group">
              <Button
                type="button"
                onClick={handler}
                display="flex"
                disabled={loading}
                textAlign="text-left"
              >
                <Marker type="numeric" ordinal={0} color={markerClass} />

                <Text as="span" padding="pl-[0.5em]" color={textColorClass}>
                  Login with Google
                </Text>
              </Button>
            </div>
            <ListItem group>
              <Marker type="numeric" ordinal={1} color={markerClass} />
              <Text
                as="a"
                href="https://fragment.dev/get-access"
                padding="pl-[0.5em]"
                color={textColorClass}
              >
                Get Access
              </Text>
            </ListItem>
          </List>
        )}
        {shouldRenderMessageContainer && (
          <div className="mt-[4em]">
            {message && <Text fontSize="text-fnormal">{message}</Text>}
            {errorMessage && (
              <ErrorText fontSize="text-fxl">{errorMessage}</ErrorText>
            )}
          </div>
        )}
      </div>
      <FullPage.Footer>
        <div className="w-f33">
          <Text fontSize="text-fnormal">
            By logging in, you agree to our{' '}
            <Text
              as="a"
              href="https://fragment.dev/legal"
              color="text-main-600 hover:text-main"
              transition="transition-colors"
              fontDecoration="underline"
            >
              terms of use and privacy policy
            </Text>
            .
          </Text>
        </div>
      </FullPage.Footer>
    </FullPage.Layout>
  );
};
